<div class="table-toolbar">
  <mat-form-field appearance="outline" class="search-field">
    <mat-label>Search all records</mat-label>
    <mat-icon matPrefix>search</mat-icon>
    <input matInput
           [value]="searchValue"
           (input)="applySearch($event.target.value)"
           aria-label="Search the 4GLTE report">
    <button mat-icon-button matSuffix
            *ngIf="searchValue"
            type="button"
            aria-label="Clear search"
            (click)="applySearch('')">
      <mat-icon>close</mat-icon>
    </button>
  </mat-form-field>
  <mat-checkbox [checked]="duplicatesOnly" (change)="toggleDuplicates($event.checked)">Duplicates only</mat-checkbox>
  <mat-checkbox [(ngModel)]="identifiersView">Identifiers view</mat-checkbox>
  <div class="record-count" aria-live="polite">
    <strong>{{ dataSource.filteredData.length | number }}</strong>
    of {{ rows.length | number }} records
  </div>
</div>

<details class="billing-help"><summary>Usage scope &amp; SIM identifiers</summary><p>Rows below 0.001 GB verified usage are hidden here. Pooling Data uses the complete License roster. Different ICCIDs are flagged for investigation, not automatically treated as errors.</p></details>

<details class="duplicate-list" *ngIf="duplicateMacs.length" [open]="duplicatesOnly">
  <summary>{{ duplicateMacs.length | number }} unique duplicate MACs · {{ duplicateRowCount | number }} affected report rows</summary>
  <div class="duplicate-macs"><button mat-stroked-button type="button" *ngFor="let mac of duplicateMacs" (click)="viewDuplicateMac(mac)" [attr.aria-label]="'Show duplicate rows for MAC ' + mac">{{ mac }}</button></div>
</details>

<div class="usage-table-shell" tabindex="0" aria-label="Scrollable 4GLTE billing report">
  <table mat-table
         matSort
         [dataSource]="dataSource"
         matSortActive="cpUsageGb"
         matSortDirection="desc"
         [trackBy]="trackByRow"
         class="usage-table"
         [class.read-only]="readOnly">
    <ng-container matColumnDef="mac" sticky>
      <th mat-header-cell *matHeaderCellDef mat-sort-header>MAC / Device</th>
      <td mat-cell *matCellDef="let row">
        <div class="mac-cell">
          <strong>{{ row.mac || 'Invalid MAC' }}</strong>
          <span class="device-name" *ngIf="row.deviceName?.trim()">{{ row.deviceName }}</span>
          <details class="sim-details" *ngIf="!identifiersView && (row.licenseIccid?.trim() || row.eid?.trim() || row.tealBootstrapIccid?.trim() || row.simSid?.trim() || row.koreIccid?.trim() || row.deviceGroup?.trim() || row.firmwareVersion?.trim())">
            <summary>SIM details <span *ngIf="identifiersDiffer(row)" class="identifier-difference">· Different ICCIDs</span></summary>
            <dl>
              <ng-container *ngIf="row.licenseIccid?.trim()"><dt>License · ICCID</dt><dd>{{ row.licenseIccid }}</dd></ng-container>
              <ng-container *ngIf="row.eid?.trim()"><dt>Teal · eID</dt><dd>{{ row.eid }}</dd></ng-container>
              <ng-container *ngIf="row.tealBootstrapIccid?.trim()"><dt>Teal · Bootstrap ICCID</dt><dd>{{ row.tealBootstrapIccid }}</dd></ng-container>
              <ng-container *ngIf="row.simSid?.trim()"><dt>KORE · SIM SID</dt><dd>{{ row.simSid }}</dd></ng-container>
              <ng-container *ngIf="row.koreIccid?.trim()"><dt>KORE · ICCID</dt><dd>{{ row.koreIccid }}</dd></ng-container>
              <ng-container *ngIf="row.deviceGroup?.trim()"><dt>Device group</dt><dd>{{ row.deviceGroup }}</dd></ng-container>
              <ng-container *ngIf="row.firmwareVersion?.trim()"><dt>Firmware</dt><dd>{{ row.firmwareVersion }}</dd></ng-container>
            </dl>
          </details>
          <span class="duplicate-badge" *ngIf="row.hasDuplicateMac">
            <mat-icon>content_copy</mat-icon>
            Duplicate: {{ row.duplicateSources.join(', ') }}
          </span>
        </div>
      </td>
    </ng-container>

    <ng-container matColumnDef="eid">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Teal · eID</th>
      <td mat-cell *matCellDef="let row">{{ row.eid || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="simSid">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>KORE · SIM SID</th>
      <td mat-cell *matCellDef="let row">{{ row.simSid || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="licenseIccid">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>License ICCID</th>
      <td mat-cell *matCellDef="let row">{{ row.licenseIccid || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="tealBootstrapIccid">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Teal Bootstrap ICCID</th>
      <td mat-cell *matCellDef="let row">{{ row.tealBootstrapIccid || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="koreIccid">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>KORE ICCID</th>
      <td mat-cell *matCellDef="let row">{{ row.koreIccid || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="tealUsageGb">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Teal usage (GB)</th>
      <td mat-cell *matCellDef="let row" class="number-cell">{{ formatGb(row.tealUsageGb) }}</td>
    </ng-container>

    <ng-container matColumnDef="koreUsageGb">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>KORE usage (GB)</th>
      <td mat-cell *matCellDef="let row" class="number-cell">{{ formatGb(row.koreUsageGb) }}</td>
    </ng-container>

    <ng-container matColumnDef="cpUsageGb">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>CP usage (GB)</th>
      <td mat-cell *matCellDef="let row" class="number-cell">{{ formatGb(row.cpUsageGb) }}</td>
    </ng-container>

    <ng-container matColumnDef="verificationSource">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Verified from</th>
      <td mat-cell *matCellDef="let row">
        <span *ngIf="readOnly">{{ row.verificationSource === 'cp' ? 'License / CP' : row.verificationSource === 'kore' ? 'KORE' : 'Teal' }}</span>
        <mat-select *ngIf="!readOnly" class="table-select"
                    [(ngModel)]="row.verificationSource"
                    (selectionChange)="updateVerification(row)"
                    [attr.aria-label]="'Verification source for ' + row.mac">
          <mat-option *ngFor="let source of verificationSources" [value]="source.value">
            {{ source.label }}
          </mat-option>
        </mat-select>
      </td>
    </ng-container>

    <ng-container matColumnDef="verificationGb">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Verification (GB)</th>
      <td mat-cell *matCellDef="let row" class="number-cell verification-value">
        {{ formatGb(row.verificationGb) }}
      </td>
    </ng-container>

    <ng-container matColumnDef="gbAt95Percent">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>GB @ 95% overage</th>
      <td mat-cell *matCellDef="let row" class="number-cell">{{ formatGb(row.gbAt95Percent) }}</td>
    </ng-container>

    <ng-container matColumnDef="gigsToCharge">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Gigs to charge</th>
      <td mat-cell *matCellDef="let row" class="number-cell charge-value">{{ row.gigsToCharge }}</td>
    </ng-container>

    <ng-container matColumnDef="bill">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Bill</th>
      <td mat-cell *matCellDef="let row">
        <span *ngIf="readOnly">{{ row.bill ? 'Yes' : 'No' }}</span>
        <mat-select *ngIf="!readOnly" class="bill-select"
                    [(ngModel)]="row.bill"
                    (selectionChange)="updateBill(row)"
                    [attr.aria-label]="'Billing decision for ' + row.mac">
          <mat-option [value]="true">Yes</mat-option>
          <mat-option [value]="false">No</mat-option>
        </mat-select>
      </td>
    </ng-container>

    <ng-container matColumnDef="deviceGroup">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Device group</th>
      <td mat-cell *matCellDef="let row">{{ row.deviceGroup || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="deviceName">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Device name</th>
      <td mat-cell *matCellDef="let row">{{ row.deviceName || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="firmwareVersion">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Firmware</th>
      <td mat-cell *matCellDef="let row">{{ row.firmwareVersion || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="networkPlan">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Network plan</th>
      <td mat-cell *matCellDef="let row">{{ row.networkPlan || '—' }}</td>
    </ng-container>

    <ng-container matColumnDef="remarks">
      <th mat-header-cell *matHeaderCellDef>Remarks</th>
      <td mat-cell *matCellDef="let row">
        <span *ngIf="readOnly">{{ row.remarks || '—' }}</span>
        <input *ngIf="!readOnly" class="remarks-input"
               [(ngModel)]="row.remarks"
               (ngModelChange)="edited.emit()"
               maxlength="500"
               [attr.aria-label]="'Remarks for ' + row.mac"
               placeholder="Add a note">
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
    <tr mat-row
        *matRowDef="let row; columns: displayedColumns"
        [class.duplicate-row]="row.hasDuplicateMac"
        [class.invalid-row]="row.hasInvalidMac"></tr>
  </table>
</div>

<div class="empty-state" *ngIf="dataSource.filteredData.length === 0">
  <mat-icon>search_off</mat-icon>
  <strong>No matching records</strong>
  <span>Try a different MAC, device, ICCID, group, or plan.</span>
</div>
